/**
 * Custom hook for managing amenities
 */
import { useState, useCallback } from 'react';
import { fetchAllAmenities, fetchAmenitiesByCategory, createAmenity, updateAmenity } from '../services/amenityService';
import { PRIMARY_CATEGORIES } from '../constants/amenities';

export const useAmenities = () => {
  const [categories, setCategories] = useState([]);
  const [selectedAmenities, setSelectedAmenities] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch and organize amenities by category
   */
  const fetchAmenities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchAllAmenities();
      
      if (response && response.categories) {
        const categoriesData = response.categories;
        const categoriesMap = {};
        
        // Initialize with primary categories
        PRIMARY_CATEGORIES.forEach(cat => {
          categoriesMap[cat.value] = {
            ...cat,
            amenities: [],
            selectedCount: 0,
          };
        });

        // Populate with data from API
        categoriesData.forEach(catData => {
          if (categoriesMap[catData.category]) {
            categoriesMap[catData.category].amenities = catData.amenities || [];
            categoriesMap[catData.category].selectedCount = catData.amenities?.length || 0;
          } else {
            categoriesMap[catData.category] = {
              value: catData.category,
              label: catData.category_display,
              amenities: catData.amenities || [],
              selectedCount: catData.amenities?.length || 0,
            };
          }
        });

        // Convert to array and sort by primary categories first
        const sortedCategories = PRIMARY_CATEGORIES.map(cat => 
          categoriesMap[cat.value] || {
            ...cat,
            amenities: [],
            selectedCount: 0,
          }
        );

        // Add other categories
        Object.keys(categoriesMap).forEach(key => {
          if (!PRIMARY_CATEGORIES.find(c => c.value === key)) {
            sortedCategories.push(categoriesMap[key]);
          }
        });

        setCategories(sortedCategories);

        // Initialize selected amenities
        const selected = {};
        sortedCategories.forEach(cat => {
          selected[cat.value] = cat.amenities.map(a => a.id);
        });
        setSelectedAmenities(selected);
      }
    } catch (err) {setError(err.message || 'Failed to load amenities');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Toggle amenity selection
   */
  const toggleAmenity = useCallback((categoryValue, amenityId) => {
    setSelectedAmenities(prev => {
      const currentSelected = prev[categoryValue] || [];
      const isSelected = currentSelected.includes(amenityId);
      
      return {
        ...prev,
        [categoryValue]: isSelected
          ? currentSelected.filter(id => id !== amenityId)
          : [...currentSelected, amenityId],
      };
    });
  }, []);

  /**
   * Remove amenity from selection
   */
  const removeAmenity = useCallback((categoryValue, amenityId) => {
    setSelectedAmenities(prev => ({
      ...prev,
      [categoryValue]: (prev[categoryValue] || []).filter(id => id !== amenityId),
    }));
  }, []);

  /**
   * Set selected amenities for a category
   */
  const setCategorySelectedAmenities = useCallback((categoryValue, amenityIds) => {
    setSelectedAmenities(prev => ({
      ...prev,
      [categoryValue]: amenityIds,
    }));
  }, []);

  return {
    categories,
    setCategories,
    selectedAmenities,
    setSelectedAmenities,
    loading,
    error,
    fetchAmenities,
    toggleAmenity,
    removeAmenity,
    setCategorySelectedAmenities,
  };
};

